import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, ArrowUpRight, Building2, Factory, Store, TrendingDown, LineChart, ShieldCheck } from "lucide-react";
import { PageHero } from "../components/PageHero";
import { ContactForm } from "../components/ContactForm";
import { Reveal } from "../components/Reveal";
import { Seo } from "../components/Seo";
import { FaqSection } from "../sections/FaqSection";
import { useUI } from "../context/UIContext";
import { IMAGES } from "../data/content";

const SEGMENTS = [
  {
    key: "mittelstand",
    icon: Building2,
    title: "Mittelstand",
    text: "Vom wachsenden Betrieb bis zum etablierten KMU: Wir strukturieren Ihren Stromeinkauf, prüfen Laufzeiten und Preisgarantien und sorgen für planbare Kosten – ohne internen Verwaltungsaufwand.",
    points: ["SLP- & RLM-Verträge geprüft", "Transparente Konditionen", "Fester Ansprechpartner"],
    to: "/gewerbekunden",
  },
  {
    key: "industrie",
    icon: Factory,
    title: "Industrie & Produktion",
    text: "Für hohe Verbräuche mit RLM-Messung nutzen wir börsennahe Spotmarkt-Beschaffung – ohne die überhöhten Risiko- und Spekulationszuschläge des Terminmarkts. Individuelle Lastprofilanalyse inklusive.",
    points: ["Börsennahe Spotmarkt-Beschaffung", "RLM-Lastprofilanalyse", "Reduzierte Risikozuschläge"],
    to: "/branche/industrie",
  },
  {
    key: "filialen",
    icon: Store,
    title: "Filialunternehmen",
    text: "Viele Standorte, viele Zählpunkte, ein Ziel: einheitliche und günstige Konditionen. Wir bündeln Ihre Entnahmestellen in einer Beschaffung und behalten Fristen sowie Verträge zentral im Blick.",
    points: ["Bündelung aller Zählpunkte", "Rahmenverträge mit klaren Konditionen", "Zentrale Vertragsverwaltung"],
    to: "/gewerbekunden",
  },
];

const VALUE = [
  { icon: TrendingDown, title: "Echtes Sparpotenzial", text: "Abseits von Vergleichsportalen erschließen wir Konditionen, die Standardangebote selten bieten." },
  { icon: LineChart, title: "Strategische Beschaffung", text: "Wir beobachten den Markt und wählen den richtigen Zeitpunkt für Ihren Abschluss – statt zum Höchststand zu verlängern." },
  { icon: ShieldCheck, title: "Planbare Budgets", text: "Strukturierte Beschaffung glättet Preisspitzen und schafft Sicherheit für Ihre Kalkulation." },
];

export default function Gewerbestrom() {
  const { openUpload } = useUI();
  const schema = {
    "@context": "https://schema.org",
    "@type": "Service",
    serviceType: "Gewerbestrom Beratung",
    provider: { "@type": "ProfessionalService", name: "Energie-Tarifberater", areaServed: "DE" },
  };

  return (
    <>
      <Seo
        title="Gewerbestrom für Mittelstand, Industrie & Filialen"
        description="Gewerbestrom unabhängig optimiert: börsennahe Beschaffung, RLM-Lastprofilanalyse und gebündelte Verträge für Mittelstand, Industrie und Filialunternehmen. Kostenlos & unverbindlich."
        path="/gewerbestrom"
        image={IMAGES.industry}
        schema={schema}
      />
      <PageHero
        eyebrow="Gewerbestrom-Hub"
        titleLines={["Gewerbestrom, der", 'sich <span class="font-serif italic text-blue-700">rechnet.</span>']}
        subtitle="Für Mittelstand, Industrie und Filialunternehmen: unabhängige Beschaffung, individuelle Lastprofilanalyse und planbare Energiekosten – persönlich betreut statt über ein anonymes Portal."
        image={IMAGES.industry}
        breadcrumb={[{ label: "Gewerbekunden", to: "/gewerbekunden" }, { label: "Gewerbestrom" }]}
      />

      {/* Intro + Value */}
      <section className="bg-background py-24 sm:py-28">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10">
          <Reveal>
            <p className="text-2xl sm:text-3xl font-heading font-semibold text-navy-900 leading-snug max-w-4xl mb-16">
              Gewerblicher Stromeinkauf folgt eigenen Regeln. Ob Standardlastprofil (SLP) oder registrierende Leistungsmessung (RLM) – wir analysieren Ihr Verbrauchsprofil und beschaffen Strom börsennah, transparent und zum richtigen Zeitpunkt.
            </p>
          </Reveal>
          <div className="grid md:grid-cols-3 gap-6">
            {VALUE.map((v, i) => {
              const Icon = v.icon;
              return (
                <Reveal key={v.title} delay={i * 0.08}>
                  <div className="rounded-3xl border border-slate-200 bg-white p-8 h-full" data-testid={`gewerbestrom-value-${i}`}>
                    <span className="inline-grid place-items-center h-12 w-12 rounded-2xl bg-blue-50 text-blue-700 mb-5"><Icon className="h-6 w-6" /></span>
                    <h3 className="text-xl font-heading text-navy-900 mb-2">{v.title}</h3>
                    <p className="text-slate-500 text-sm leading-relaxed">{v.text}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* Segments */}
      <section className="bg-slate-50 py-24 sm:py-28">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10">
          <Reveal>
            <p className="text-xs uppercase tracking-[0.25em] text-blue-700 mb-4">Für Ihr Unternehmen</p>
            <h2 className="text-3xl sm:text-5xl font-heading font-semibold text-navy-900 mb-14 max-w-3xl">Passgenau für jede Unternehmensgröße</h2>
          </Reveal>
          <div className="grid md:grid-cols-3 gap-6">
            {SEGMENTS.map((s, i) => {
              const Icon = s.icon;
              return (
                <Reveal key={s.key} delay={i * 0.08}>
                  <div className="group flex flex-col h-full rounded-3xl border border-slate-200 bg-white p-8 hover:-translate-y-1.5 hover:border-blue-300 hover:shadow-[0_30px_60px_-35px_rgba(29,78,216,0.25)] transition-[transform,border-color,box-shadow] duration-500" data-testid={`gewerbestrom-segment-${s.key}`}>
                    <span className="inline-grid place-items-center h-14 w-14 rounded-2xl bg-blue-50 text-blue-700 group-hover:bg-blue-700 group-hover:text-white transition-colors duration-500 mb-6"><Icon className="h-6 w-6" /></span>
                    <h3 className="text-xl font-heading text-navy-900 mb-3">{s.title}</h3>
                    <p className="text-slate-500 text-sm leading-relaxed mb-6">{s.text}</p>
                    <ul className="space-y-2 mb-8">
                      {s.points.map((p, j) => (
                        <li key={j} className="flex gap-2 text-sm text-navy-900"><Check className="h-4 w-4 text-blue-600 mt-0.5 shrink-0" strokeWidth={2.5} /> {p}</li>
                      ))}
                    </ul>
                    <Link to={s.to} className="mt-auto inline-flex items-center gap-1.5 text-sm font-medium text-blue-700 hover:gap-2.5 transition-all" data-testid={`gewerbestrom-segment-link-${s.key}`}>
                      Mehr erfahren <ArrowUpRight className="h-4 w-4" />
                    </Link>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* SLP vs RLM + Lead form */}
      <section className="bg-background py-24 sm:py-28">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-7">
            <Reveal>
              <h2 className="text-2xl font-heading text-navy-900 mb-6">SLP oder RLM – wo stehen Sie?</h2>
              <div className="grid sm:grid-cols-2 gap-6 mb-10">
                <div className="rounded-3xl border border-slate-200 bg-white p-7" data-testid="gewerbestrom-slp">
                  <p className="text-xs uppercase tracking-[0.2em] text-blue-700 mb-3">bis 100.000 kWh / Jahr</p>
                  <h3 className="text-lg font-heading text-navy-900 mb-2">Standardlastprofil (SLP)</h3>
                  <p className="text-slate-500 text-sm leading-relaxed">Für kleinere und mittlere Betriebe. Wir vergleichen Tarife anbieterübergreifend und sichern faire, planbare Konditionen ohne versteckte Klauseln.</p>
                </div>
                <div className="rounded-3xl border border-slate-200 bg-white p-7" data-testid="gewerbestrom-rlm">
                  <p className="text-xs uppercase tracking-[0.2em] text-blue-700 mb-3">ab 100.000 kWh / Jahr</p>
                  <h3 className="text-lg font-heading text-navy-900 mb-2">Registrierende Leistungsmessung (RLM)</h3>
                  <p className="text-slate-500 text-sm leading-relaxed">Für hohe Verbräuche mit viertelstündlicher Messung. Hier entfalten börsennahe Beschaffung und individuelle Lastprofilanalyse ihr volles Potenzial.</p>
                </div>
              </div>
              <div className="rounded-3xl bg-blue-700 p-10 flex flex-col sm:flex-row items-center justify-between gap-6">
                <div>
                  <p className="text-4xl font-heading font-semibold text-blue-100">Nicht sicher, welche Vertragsart passt?</p>
                  <p className="text-slate-300 mt-2">Laden Sie Ihre Rechnung hoch – wir ordnen Ihr Profil kostenlos ein.</p>
                </div>
                <button onClick={openUpload} className="group rounded-full bg-white text-blue-700 hover:bg-blue-50 px-8 py-4 font-medium transition-colors flex items-center gap-2 whitespace-nowrap" data-testid="gewerbestrom-upload-btn">
                  Rechnung prüfen <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </button>
              </div>
            </Reveal>
          </div>

          <div className="lg:col-span-5">
            <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.7 }}
              className="lg:sticky lg:top-28 rounded-3xl border border-slate-200 bg-white p-8 shadow-[0_40px_80px_-40px_rgba(0,0,0,0.15)]">
              <p className="text-xs uppercase tracking-[0.2em] text-blue-700 mb-2">Gewerbestrom-Analyse</p>
              <h3 className="text-2xl font-heading font-semibold text-navy-900 mb-6">Unverbindliches <span className="font-serif italic text-blue-700">Angebot</span></h3>
              <ContactForm compact topic="gewerbestrom" defaultType="beratung" testid="gewerbestrom-form" />
            </motion.div>
          </div>
        </div>
      </section>

      <FaqSection />
    </>
  );
}
